import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, effect, inject, PLATFORM_ID } from '@angular/core';
import { getState, patchState, signalStoreFeature, type, withHooks, withMethods, withState } from '@ngrx/signals';
// Note: Using only public APIs for Angular 20 compatibility
import type {
  Prettify,
  SignalStoreFeatureResult,
  StateSignals,
  WritableStateSource,
} from '@ngrx/signals';

/**
 * https://github.com/gabrielguerrero/ngrx-traits/tree/main/libs/ngrx-traits/signals
 *
 * Awaiting angular 18 version to be released. Then it must be installed as a package in the project
 * npm i @ngrx-traits/signals --save
 */

/**
 * Sync the state of the store to the web storage
 * @param key - the key to use in the web storage
 * @param type - 'session' or 'local' storage
 * @param saveStateChangesAfterMs - save the state to the storage after this many milliseconds, 0 to disable
 * @param restoreOnInit - restore the state from the storage on init
 * @param filterState - filter the state before saving to the storage
 * @param onRestore - callback after the state is restored from the storage
 *
 * @example
 * const store = signalStore(
 *  // following are not required, just an example it can have anything
 *  withEntities({ entity, collection }),
 *  withCallStatus({ prop: collection, initialValue: 'loading' }),
 *
 *  withSyncToWebStorage({
 *      key: 'my-key',
 *      type: 'session',
 *      restoreOnInit: true,
 *      saveStateChangesAfterMs: 300,
 *      // optionally, filter the state before saving to the storage
 *      filterState: ({ orderItemsEntityMap, orderItemsIds }) => ({
 *       orderItemsEntityMap,
 *       orderItemsIds,
 *     }),
 *  }),
 *  );
 *  // generates the following methods
 *  store.saveToStorage();
 *  store.loadFromStorage();
 *  store.clearFromStore();
 */
/** The store as the host's own features see it — what `onRestore` gets. */
type HostStore<Input extends SignalStoreFeatureResult> = Prettify<
  StateSignals<Input['state']> &
    Input['props'] &
    Input['methods'] &
    WritableStateSource<Input['state']>
>;

/**
 * `Input` is generic inside the feature, so NgRx cannot narrow the store to a state source by
 * itself; this is the one place that says it is one.
 */
const asStateSource = (store: unknown) => store as WritableStateSource<Record<string, unknown>>;

export function withSyncToWebStorage<Input extends SignalStoreFeatureResult>({
  key,
  type: storageType,
  saveStateChangesAfterMs = 500,
  restoreOnInit = true,
  filterState,
  beforeRestore,
  onRestore,
}: {
  key: string;
  type: 'session' | 'local';
  restoreOnInit?: boolean;
  saveStateChangesAfterMs?: number;
  filterState?: (state: Input['state']) => unknown;
  /**
   * Transforms the raw parsed JSON *before* `patchState` sees it — for a migration that has to
   * rename or drop a key the current state does not declare. `patchState` silently drops (with a
   * console warning) any key that is not part of the store's initial state, so `onRestore` below
   * — which runs after, reading the store back — can never recover a key that never survived the
   * first patch; that class of migration has to happen here instead. Return the corrected object;
   * returning nothing leaves the parsed JSON as read.
   */
  beforeRestore?: (state: Record<string, unknown>) => Record<string, unknown> | void;
  /**
   * Runs after the stored JSON has been patched into state, for a migration that only needs to
   * reformat a value the state already declares (e.g. a `Date` field that was serialised to a
   * plain string) rather than rename or drop an unrecognised key — see `beforeRestore` for that
   * case instead.
   */
  onRestore?: (store: HostStore<Input>) => void;
}) {
  return signalStoreFeature(
    type<Input>(),
    withState({}),
    withMethods((store) => {
      const source = asStateSource(store);
      const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
      return {
        saveToStorage() {
          if (!isBrowser) return;
          const state = filterState ? filterState(getState(source)) : getState(source);
          if (storageType === 'local') window.localStorage.setItem(key, JSON.stringify(state));
          else window.sessionStorage.setItem(key, JSON.stringify(state));
        },
        loadFromStorage(): boolean {
          if (!isBrowser) {
            return false;
          }
          const stateJson =
            storageType === 'local' ? window.localStorage.getItem(key) : window.sessionStorage.getItem(key);
          if (!stateJson) {
            return false;
          }
          const parsed = JSON.parse(stateJson);
          patchState(source, beforeRestore?.(parsed) ?? parsed);
          onRestore?.(store as unknown as HostStore<Input>);
          return true;
        },
        clearFromStore() {
          if (!isBrowser) return;
          if (storageType === 'local') window.localStorage.removeItem(key);
          else window.sessionStorage.removeItem(key);
        },
      };
    }),
    withHooks(({ loadFromStorage, saveToStorage, ...store }) => {
      const destroyRef = inject(DestroyRef);

      return {
        onInit() {
          if (restoreOnInit) loadFromStorage();

          if (!saveStateChangesAfterMs) return;

          // True from a state change until that change has reached storage.
          let unsaved = false;

          effect((onCleanup) => {
            getState(asStateSource(store)); // read so the effect re-runs on every state change
            unsaved = true;

            const timeout = setTimeout(() => {
              unsaved = false;
              saveToStorage();
            }, saveStateChangesAfterMs);

            // Registered through `onCleanup`, not returned: Angular ignores an effect's return
            // value, so a returned function never ran. Every change then left its own timer
            // behind — no debounce at all, and a timer that outlived the store fired the dead
            // store's state into storage later, which in a shared test environment is the *next*
            // spec file's localStorage.
            onCleanup(() => clearTimeout(timeout));
          });

          // The effect's cleanup also runs on destroy, which would drop a change made inside the
          // last window — a column preference toggled just before navigating off a page whose
          // store dies with it. Written out instead, synchronously, so nothing is lost and
          // nothing is left pending.
          destroyRef.onDestroy(() => {
            if (unsaved) saveToStorage();
          });
        },
      };
    }),
  );
}
